import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { CreditCard, Plus, Edit2, Trash2, Loader2, Globe, ToggleLeft, ToggleRight } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { PaymentMethodForm } from './PaymentMethodForm';
import { RegionMethodForm } from './RegionMethodForm';

interface PaymentGateway {
  id: string;
  name: string;
  description: string;
  is_active: boolean;
  is_fallback: boolean;
}

interface PaymentMethod {
  id: string;
  gateway_id: string;
  name: string;
  description: string;
  icon: string;
  is_active: boolean;
}

interface Region {
  id: string;
  code: string;
  name: string;
  currency_code: string;
}

export const PaymentMethodsPage: React.FC = () => {
  const [showMethodForm, setShowMethodForm] = useState(false);
  const [showRegionMethodForm, setShowRegionMethodForm] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [selectedGateway, setSelectedGateway] = useState<PaymentGateway | null>(null);

  // Fetch payment gateways
  const { data: gateways = [], isLoading: gatewaysLoading, refetch: refetchGateways } = useQuery<PaymentGateway[]>(
    'payment-gateways',
    async () => {
      const { data, error } = await supabase
        .from('payment_gateways')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    }
  );

  // Fetch payment methods
  const { data: methods = [], isLoading: methodsLoading } = useQuery<PaymentMethod[]>(
    'payment-methods',
    async () => {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    }
  );

  // Fetch regions
  const { data: regions = [], isLoading: regionsLoading } = useQuery<Region[]>(
    'regions',
    async () => {
      const { data, error } = await supabase
        .from('regions')
        .select('id, code, name, currency_code')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data;
    }
  );

  const handleAddMethod = (gateway: PaymentGateway) => {
    setSelectedGateway(gateway);
    setSelectedMethod(null);
    setShowMethodForm(true);
  };

  const handleEditMethod = (method: PaymentMethod) => {
    const gateway = gateways.find(g => g.id === method.gateway_id);
    if (gateway) {
      setSelectedGateway(gateway);
      setSelectedMethod(method);
      setShowMethodForm(true);
    }
  };

  const handleRegionMethods = (method: PaymentMethod) => {
    setSelectedMethod(method);
    setShowRegionMethodForm(true);
  };

  const getMethodsByGateway = (gatewayId: string) => {
    return methods.filter(method => method.gateway_id === gatewayId);
  };

  const toggleGatewayFallback = async (gateway: PaymentGateway) => {
    try {
      const { error } = await supabase
        .from('payment_gateways')
        .update({ is_fallback: !gateway.is_fallback })
        .eq('id', gateway.id);
        
      if (error) throw error;
      
      // Refetch gateways to update UI
      refetchGateways();
    } catch (error) {
      console.error('Error toggling gateway fallback status:', error);
      alert('Failed to update gateway fallback status');
    }
  };

  const isLoading = gatewaysLoading || methodsLoading || regionsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Payment Methods
            </h2>
          </div>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Manage payment gateways and methods for different regions
        </p>
      </div>

      {/* Payment Gateways and Methods */}
      <div className="space-y-6">
        {gateways.map(gateway => (
          <div 
            key={gateway.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {gateway.name}
                    </h3>
                    {gateway.is_fallback && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                        Fallback
                      </span>
                    )}
                  </div>
                  {gateway.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {gateway.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleGatewayFallback(gateway)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title={gateway.is_fallback ? "Remove from fallback gateways" : "Add to fallback gateways"}
                  >
                    {gateway.is_fallback ? (
                      <>
                        <ToggleRight className="w-5 h-5 text-blue-500" />
                        <span className="text-sm">Fallback</span>
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                        <span className="text-sm">Fallback</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleAddMethod(gateway)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Method
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {getMethodsByGateway(gateway.id).length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                  No payment methods added yet
                </p>
              ) : (
                <div className="grid gap-4">
                  {getMethodsByGateway(gateway.id).map(method => (
                    <div 
                      key={method.id}
                      className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {method.name}
                        </h4>
                        {method.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {method.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRegionMethods(method)}
                          className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Manage regions"
                        >
                          <Globe className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEditMethod(method)}
                          className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Edit method"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Payment Method Form Modal */}
      {showMethodForm && selectedGateway && (
        <PaymentMethodForm
          gateway={selectedGateway}
          method={selectedMethod}
          onClose={() => {
            setShowMethodForm(false);
            setSelectedGateway(null);
            setSelectedMethod(null);
          }}
        />
      )}

      {/* Region Method Form Modal */}
      {showRegionMethodForm && selectedMethod && (
        <RegionMethodForm
          method={selectedMethod}
          regions={regions}
          onClose={() => {
            setShowRegionMethodForm(false);
            setSelectedMethod(null);
          }}
        />
      )}
    </div>
  );
};